import { useState } from 'react'
import { ChevronDown, ExternalLink, Inbox, History } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { GestorEquipeFeriasEligibility } from '@/components/Gestores/GestorEquipeFeriasEligibility'
import {
  CHAMADO_STATUS_LABELS,
  extractSolicitacaoFeriasPeriod,
  FERIAS_STATUS_LABELS,
  formatPtDate,
} from '@/components/Gestores/gestorEquipeFeriasUtils'
import type { GestorEquipeDetail } from '@/services/gestoresService'
import { cn } from '@/utils/cn'

type FeriasFormState = {
  dataInicio: string
  dataFim: string
  dias: string
  liderancaAprovada: boolean
  observacoes: string
}

type Props = {
  detail: GestorEquipeDetail
  onSubmitFerias: (form: FeriasFormState) => Promise<void>
}

function SectionHeader({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <h3 className='flex items-center gap-2 text-sm font-semibold text-text-primary'>
      {icon}
      {title}
    </h3>
  )
}

function EmptyState({ children }: { children: React.ReactNode }) {
  return (
    <p className='rounded-lg border border-dashed border-white/[0.08] px-4 py-6 text-center text-sm text-text-muted'>
      {children}
    </p>
  )
}

export function GestorEquipeFeriasPanel({ detail, onSubmitFerias }: Props) {
  const [showManualForm, setShowManualForm] = useState(false)
  const [feriasForm, setFeriasForm] = useState<FeriasFormState>({
    dataInicio: '',
    dataFim: '',
    dias: '',
    liderancaAprovada: false,
    observacoes: '',
  })

  const resumo = detail.feriasResumo
  if (!resumo) {
    return <p className='text-text-muted'>Resumo de férias indisponível.</p>
  }

  const submitManual = async (e: React.FormEvent) => {
    e.preventDefault()
    await onSubmitFerias(feriasForm)
    setFeriasForm({
      dataInicio: '',
      dataFim: '',
      dias: '',
      liderancaAprovada: false,
      observacoes: '',
    })
    setShowManualForm(false)
  }

  return (
    <div className='space-y-6'>
      <GestorEquipeFeriasEligibility detail={detail} resumo={resumo} />

      <div className='grid gap-6 lg:grid-cols-2'>
        <section className='space-y-3'>
          <SectionHeader icon={<Inbox size={16} className='text-accent' />} title='Solicitações' />
          {detail.solicitacoesFerias.length === 0 ? (
            <EmptyState>Nenhuma solicitação no portal ou financeiro.</EmptyState>
          ) : (
            <ul className='space-y-2'>
              {detail.solicitacoesFerias.map((item) => {
                const periodo = extractSolicitacaoFeriasPeriod(item.type, item.formData)
                return (
                  <li
                    key={item.id}
                    className='rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-3'
                  >
                    <div className='flex flex-wrap items-start justify-between gap-2'>
                      <div className='min-w-0'>
                        <p className='truncate font-medium text-text-primary'>
                          {item.type === 'P3_ENTRADA_PARCEIRO' ? 'Descanso PJ' : 'Ausência'} ·{' '}
                          {item.protocol}
                        </p>
                        <p className='mt-0.5 text-xs text-text-muted'>
                          {formatPtDate(item.createdAt)} ·{' '}
                          {CHAMADO_STATUS_LABELS[item.status] ?? item.status.replace(/_/g, ' ')}
                        </p>
                      </div>
                      <Link
                        to={`/gestores/chamados/${item.id}`}
                        className='inline-flex shrink-0 items-center gap-1 text-xs font-medium text-accent hover:underline'
                      >
                        Ver chamado
                        <ExternalLink size={12} />
                      </Link>
                    </div>
                    {(periodo.inicio || periodo.fim || periodo.resumo) && (
                      <div className='mt-2 border-t border-white/[0.06] pt-2 text-sm text-text-secondary'>
                        {periodo.inicio && periodo.fim && (
                          <p>
                            {formatPtDate(periodo.inicio)} → {formatPtDate(periodo.fim)}
                            {periodo.dias != null ? ` · ${periodo.dias}d` : ''}
                          </p>
                        )}
                        {periodo.resumo && (
                          <p className='mt-1 line-clamp-2 text-xs text-text-muted'>{periodo.resumo}</p>
                        )}
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </section>

        <section className='space-y-3'>
          <SectionHeader icon={<History size={16} className='text-accent' />} title='Histórico registrado' />
          {detail.ferias.length === 0 ? (
            <EmptyState>Nenhum período registrado pelo gestor.</EmptyState>
          ) : (
            <ul className='space-y-2'>
              {detail.ferias.map((f) => (
                <li
                  key={f.id}
                  className='rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2.5'
                >
                  <p className='font-medium text-text-primary'>
                    {formatPtDate(f.dataInicio)} → {formatPtDate(f.dataFim)}
                    <span className='ml-1 text-text-muted'>({f.dias}d)</span>
                  </p>
                  <p className='mt-0.5 text-xs text-text-muted'>
                    {FERIAS_STATUS_LABELS[f.status] ?? f.status.replace(/_/g, ' ')}
                    {f.liderancaAprovada ? ' · Liderança OK' : ''}
                    {f.createdBy?.name ? ` · ${f.createdBy.name}` : ''}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <div className='overflow-hidden rounded-xl border border-white/[0.06]'>
        <button
          type='button'
          className='flex w-full items-center justify-between bg-white/[0.02] px-4 py-3 text-left text-sm font-medium text-text-primary hover:bg-white/[0.04]'
          onClick={() => setShowManualForm((open) => !open)}
        >
          Registrar férias manualmente
          <ChevronDown size={16} className={cn('text-text-muted transition-transform', showManualForm && 'rotate-180')} />
        </button>
        {showManualForm && (
          <form
            className='grid gap-3 border-t border-white/[0.06] bg-white/[0.01] px-4 py-4 sm:grid-cols-2'
            onSubmit={(e) => void submitManual(e)}
          >
            <label className='block text-sm'>
              <span className='mb-1 block text-text-muted'>Início</span>
              <input
                type='date'
                required
                className='w-full rounded-lg border border-border bg-bg-main px-3 py-2'
                value={feriasForm.dataInicio}
                onChange={(e) => setFeriasForm((f) => ({ ...f, dataInicio: e.target.value }))}
              />
            </label>
            <label className='block text-sm'>
              <span className='mb-1 block text-text-muted'>Fim</span>
              <input
                type='date'
                required
                className='w-full rounded-lg border border-border bg-bg-main px-3 py-2'
                value={feriasForm.dataFim}
                onChange={(e) => setFeriasForm((f) => ({ ...f, dataFim: e.target.value }))}
              />
            </label>
            <label className='block text-sm'>
              <span className='mb-1 block text-text-muted'>Dias (máx. 20)</span>
              <input
                type='number'
                min={1}
                max={20}
                required
                className='w-full rounded-lg border border-border bg-bg-main px-3 py-2'
                value={feriasForm.dias}
                onChange={(e) => setFeriasForm((f) => ({ ...f, dias: e.target.value }))}
              />
            </label>
            <label className='flex items-center gap-2 self-end text-sm text-text-secondary'>
              <input
                type='checkbox'
                checked={feriasForm.liderancaAprovada}
                onChange={(e) => setFeriasForm((f) => ({ ...f, liderancaAprovada: e.target.checked }))}
              />
              Aprovado pela liderança focal
            </label>
            <label className='block text-sm sm:col-span-2'>
              <span className='mb-1 block text-text-muted'>Observações</span>
              <textarea
                rows={2}
                className='w-full rounded-lg border border-border bg-bg-main px-3 py-2'
                value={feriasForm.observacoes}
                onChange={(e) => setFeriasForm((f) => ({ ...f, observacoes: e.target.value }))}
              />
            </label>
            <Button type='submit' size='sm' className='sm:col-span-2'>
              Registrar férias
            </Button>
          </form>
        )}
      </div>
    </div>
  )
}
